import { useState } from 'react';
import { cx } from '@twind/core';
import { useTranslation } from 'react-i18next';
import Drawer from 'app/components/Drawer';
import MiniSelectionCard from 'app/components/MiniSelectionCard';
import Input from 'app/components/form/Input';
import useFeedbackAnalytics, { FeedbackType, UserQueryType } from 'app/hooks/fetch/useFeedbackAnalytics';

interface FeedbackDrawerProps {
  isOpen: boolean;
  onClose?: () => void;
  onSubmitFeedback?: (feedback: { selectedOption: string; comment: string }) => void;
  onSkipFeedback?: () => void;
  userQuery?: string;
  resultType?: string;
  userQueryType: UserQueryType;
  feedbackType: FeedbackType;
  secondaryFeedbackOptions?: readonly string[];
  secondaryFeedbackHeader?: string;
  otherFieldEnabled?: boolean;
}

export default function FeedbackDrawer({
  isOpen,
  onClose,
  onSubmitFeedback,
  onSkipFeedback,
  userQuery,
  resultType,
  userQueryType,
  feedbackType,
  secondaryFeedbackOptions,
  secondaryFeedbackHeader,
  otherFieldEnabled,
}: FeedbackDrawerProps) {
  const { t } = useTranslation();
  const { captureSecondaryFeedbackSubmitted, captureSecondaryFeedbackSkipped } = useFeedbackAnalytics();

  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const showOtherField = otherFieldEnabled && selectedOption;

  const handleOptionSelect = (option: string) => {
    setSelectedOption(option);
  };

  const handleSubmit = async () => {
    if (!selectedOption) return;
    captureSecondaryFeedbackSubmitted({
      feedbackType,
      userQuery,
      userQueryType,
      resultType,
      secondaryReason: selectedOption,
      secondaryReasonComment: comment,
      secondaryReasonOptions: secondaryFeedbackOptions as string[],
    });
    onSubmitFeedback?.({ selectedOption, comment });
    onClose?.();
  };

  const handleSkip = async () => {
    captureSecondaryFeedbackSkipped({
      feedbackType,
      userQuery,
      userQueryType,
      resultType,
    });
    onSkipFeedback?.();
    onClose?.();
  };

  const handleClose = () => {
    captureSecondaryFeedbackSkipped({
      feedbackType,
      userQuery,
      userQueryType,
      resultType,
    });
    setSelectedOption(null);
    setComment('');
    onClose?.();
  };

  const isSubmitDisabled = !selectedOption;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
    >
      <div className="flex flex-col gap-4 px-4 py-6 ">
        <h2 className="text-sm font-semibold text-typography-heading mb-2 leading-[130%]">{secondaryFeedbackHeader}</h2>

        <div className="flex flex-wrap gap-3">
          {secondaryFeedbackOptions?.map((option) => (
            <MiniSelectionCard
              key={option}
              onClick={() => handleOptionSelect(option)}
              selected={selectedOption === option}
            >
              {option}
            </MiniSelectionCard>
          ))}
        </div>

        <div
          className={cx(
            'overflow-hidden transition-[height] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]',
            showOtherField ? 'h-[46px]' : 'h-0'
          )}
        >
          {showOtherField && (
            <Input
              id="feedback-comment"
              name="feedback-comment"
              placeholder={t('feedback.comment_placeholder')}
              value={comment}
              onChange={setComment}
              customConfig={{
                maxLength: 150,
              }}
            />
          )}
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className={cx(
              'w-full py-3 px-6 rounded-lg font-medium transition-all duration-200',
              isSubmitDisabled
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-typography-heading text-white hover:bg-gray-800'
            )}
          >
            {t('feedback.submit_button')}
          </button>

          <button
            onClick={handleSkip}
            className={cx('text-center text-sm text-black font-medium')}
          >
            {t('feedback.skip_button')}
          </button>
        </div>
      </div>
    </Drawer>
  );
}
